import { useEffect, useMemo, useRef, useState } from 'react';
import {
  deleteSavedView,
  loadSavedViews,
  saveSavedView,
  type MemoryQuerySnapshot,
  type MemorySavedView,
} from './savedViews';
import '@/src/styles/orgs.css';

/**
 * The ⌘K palette (memory tier 2): fuzzy search over three sources —
 * (a) records currently loaded (jump = select + scroll), (b) the viewer's
 * personal saved views, (c) panel actions (focus mode, clear filters, save
 * the current view). Keyboard-navigable (↑/↓ move, Enter runs, Escape
 * closes), with the dialog + listbox roles assistive tech expects.
 *
 * No fuzzy dependency: substring + a rank heuristic (prefix beats word-start
 * beats mid-word) is enough at loaded-graph scale — the corpus is what the
 * viewer's own reads disclosed, typically dozens of records.
 */

/**
 * The rank of one candidate: 0 = prefix match, 1 = word-start match, 2 =
 * mid-word match, null = no match. The empty query matches everything at
 * rank 0 (the palette opens as a menu, not a blank).
 */
export function rankMatch(query: string, text: string): number | null {
  const q = query.trim().toLowerCase();
  const t = text.toLowerCase();
  if (q === '') return 0;
  if (t.startsWith(q)) return 0;
  const at = t.indexOf(q);
  if (at === -1) return null;
  const prev = at === 0 ? ' ' : t[at - 1]!;
  return /[^a-z0-9]/.test(prev) ? 1 : 2;
}

/** One navigable row. */
interface PaletteOption {
  domId: string;
  label: string;
  hint: string;
  kind: 'record' | 'view' | 'action';
  run: () => void;
  /** Saved views carry their own delete affordance. */
  deletable?: string;
}

/** The query at its default — used to decide whether "clear filters" and
 *  "save view" are honest actions (nothing to clear, nothing to save). */
function isDefaultQuery(q: MemoryQuerySnapshot): boolean {
  return q.terms.trim() === '' && q.type === null && q.privacy === null && q.scope === null;
}

export interface MemoryPaletteRecord {
  id: string;
  title: string;
  type: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Saved views are namespaced per vault — the SAME key every read uses. */
  vaultId: string;
  /** Records currently loaded (graph nodes + panel rows, deduped). */
  records: MemoryPaletteRecord[];
  /** The query as it stands — what "save view" persists and restores. */
  currentQuery: MemoryQuerySnapshot;
  /** Focus-mode state (the action toggles it). */
  focusOn: boolean;
  /** Whether focusing is meaningful right now (a selection exists). */
  canFocus: boolean;
  onToggleFocus: () => void;
  onClearFilters: () => void;
  onRestoreView: (query: MemoryQuerySnapshot) => void;
  onJumpToRecord: (id: string) => void;
}

export function MemoryCommandPalette({
  open, onClose, vaultId, records, currentQuery, focusOn, canFocus,
  onToggleFocus, onClearFilters, onRestoreView, onJumpToRecord,
}: Props) {
  const [text, setText] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  // The save-view flow: the input becomes the NAME field; Enter commits.
  const [naming, setNaming] = useState(false);
  const [views, setViews] = useState<MemorySavedView[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Every open starts clean — text, cursor, mode — and re-reads storage so a
  // view saved in another tab (or a leftover of a closed session) shows.
  useEffect(() => {
    if (open) {
      setText('');
      setActiveIndex(0);
      setNaming(false);
      setViews(loadSavedViews(vaultId));
      inputRef.current?.focus();
    }
  }, [open, vaultId]);

  const options = useMemo<PaletteOption[]>(() => {
    if (naming) return [];
    // The fuzzy filter: rank each candidate's SEARCH TEXT against the query,
    // keep the matches, best rank first (ties: the shorter label wins — at
    // loaded-graph scale that is a stable, honest order).
    const filter = <T,>(
      candidates: T[],
      textOf: (c: T) => string,
      make: (c: T) => PaletteOption,
    ): PaletteOption[] => candidates
      .map((c) => ({ c, rank: rankMatch(text, textOf(c)) }))
      .filter((x): x is { c: T; rank: number } => x.rank !== null)
      .sort((a, b) => a.rank - b.rank || textOf(a.c).length - textOf(b.c).length)
      .map((x) => make(x.c));
    // The action vocabulary: focus mode (only with a selection to focus on),
    // and clearing/saving the filters (only when something is set — offering
    // a no-op would be speculation).
    const dirty = !isDefaultQuery(currentQuery);
    const actions: PaletteOption[] = [];
    if (canFocus) {
      actions.push({
        domId: 'memory-palette-action-focus',
        label: focusOn ? 'Exit focus mode' : 'Focus on the selection’s neighbours',
        hint: 'action',
        kind: 'action',
        run: () => { onToggleFocus(); onClose(); },
      });
    }
    if (dirty) {
      actions.push({
        domId: 'memory-palette-action-clear',
        label: 'Clear filters',
        hint: 'action',
        kind: 'action',
        run: () => { onClearFilters(); onClose(); },
      });
      actions.push({
        domId: 'memory-palette-action-save',
        label: 'Save current view',
        hint: 'action',
        kind: 'action',
        // Saving stays IN the palette: the input becomes the name field.
        run: () => setNaming(true),
      });
    }
    // The brief's order: records, saved views, actions.
    return [
      ...filter(records, (r) => r.title || r.id, (r) => ({
        domId: `memory-palette-record-${r.id}`,
        label: r.title || r.id,
        hint: r.type ?? 'record',
        kind: 'record' as const,
        run: () => { onJumpToRecord(r.id); onClose(); },
      })),
      ...filter(views, (v) => v.name, (v) => ({
        domId: `memory-palette-view-${v.name}`,
        label: v.name,
        hint: 'saved view',
        kind: 'view' as const,
        run: () => { onRestoreView(v.query); onClose(); },
        deletable: v.name,
      })),
      ...filter(actions, (a) => a.label, (a) => a),
    ];
  }, [naming, text, records, views, focusOn, canFocus, currentQuery, onClose, onJumpToRecord, onRestoreView, onToggleFocus, onClearFilters]);

  const active = Math.min(activeIndex, Math.max(0, options.length - 1));

  function runOption(option: PaletteOption) {
    option.run();
  }

  function removeView(name: string) {
    setViews(deleteSavedView(vaultId, name));
    setActiveIndex(0);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (naming) {
        const name = text.trim();
        if (name !== '') setViews(saveSavedView(vaultId, { name, query: currentQuery }));
        onClose();
        return;
      }
      const option = options[active];
      if (option !== undefined) runOption(option);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (options.length === 0) return;
      setActiveIndex((i) => (e.key === 'ArrowDown'
        ? Math.min(i + 1, options.length - 1)
        : Math.max(i - 1, 0)));
    }
  }

  if (!open) return null;

  return (
    <div className="vault-orgs__palette-overlay">
      <div
        className="vault-orgs__palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        data-testid="memory-palette"
      >
        <input
          ref={inputRef}
          className="vault-orgs__palette-input"
          role="combobox"
          aria-expanded={!naming}
          aria-controls="memory-palette-list"
          aria-activedescendant={naming || options.length === 0 ? undefined : options[active]?.domId}
          aria-label={naming ? 'Name for the saved view' : 'Search records, saved views and actions'}
          data-testid="memory-palette-input"
          placeholder={naming ? 'Name this view…' : 'Records, saved views, actions…'}
          value={text}
          onChange={(e) => { setText(e.target.value); setActiveIndex(0); }}
          onKeyDown={onKeyDown}
        />
        {naming ? (
          <p className="vault-orgs__palette-hint" data-testid="memory-palette-naming">
            Enter saves the current terms and chips as this view · Escape closes
          </p>
        ) : (
          <ul
            className="vault-orgs__palette-list"
            role="listbox"
            id="memory-palette-list"
            aria-label="Palette results"
            data-testid="memory-palette-list"
          >
            {options.length === 0 && (
              <li className="vault-orgs__palette-empty" role="presentation">Nothing matches</li>
            )}
            {options.map((o, i) => (
              <li
                key={o.domId}
                id={o.domId}
                role="option"
                aria-selected={i === active}
                // The label + its kind as ONE name, so a screen reader (and
                // a role query) never has to parse concatenated spans.
                aria-label={`${o.label} · ${o.hint}`}
                className={`vault-orgs__palette-option${i === active ? ' vault-orgs__palette-option--active' : ''}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => runOption(o)}
              >
                <span className="vault-orgs__palette-option-label">{o.label}</span>
                <span className="vault-orgs__palette-option-hint">{o.hint}</span>
                {o.deletable !== undefined && (
                  <button
                    type="button"
                    className="vault-orgs__palette-delete"
                    aria-label={`Delete view ${o.deletable}`}
                    data-testid="memory-palette-delete"
                    onClick={(e) => { e.stopPropagation(); removeView(o.deletable!); }}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
